/** Pure logic for the Mail Order Batch screen. */
import type { DeskPlan, HistoryItem, MemberRecord, PaymentLineBody } from './api'
import { balance, linesTotal, type ManualMethod, dollars } from './payment'
import { recipientKey, recipientLineFields, type Recipient } from './recipient'

/** One line of an envelope. A gift line (`for: 'gift'`) carries its recipient. */
export interface Line { slug: string; plan_id: string; price_cents: number; for?: 'payer' | 'gift'; rcpt?: Recipient }

/** What each line sends to POST /api/desk/payments/offline. */
export function batchLinesBody(lines: Line[]): PaymentLineBody[] {
  return lines.map((l) => {
    const base = { publication_slug: l.slug, plan_id: l.plan_id, price_cents: l.price_cents }
    return l.for === 'gift' && l.rcpt ? { ...base, for: 'gift' as const, ...recipientLineFields(l.rcpt) } : { ...base, for: 'payer' as const }
  })
}

export interface BatchEntry {
  n: number
  order_keys: string[]
  payer: { name: string | null; account_number: string | null }
  method: string
  reference: string
  lines_text: string
  amount_cents: number
  original_amount_cents: number
  voided: boolean
  partly_voided: boolean
  /** Voided after the batch was deposited (ruling R17): still counted in the deposit. */
  returned_after_deposit?: boolean
  /** A card or eCheck: charged through Authorize.Net, refunded rather than voided. */
  via_gateway?: boolean
  /** What has been refunded so far (cards and eChecks); already off `amount_cents`. */
  refunded_cents?: number
}
export interface BatchDetail {
  id: number
  label: string
  opened_at: string
  closed_at: string | null
  deposited_at: string | null
  entries: BatchEntry[]
  totals: { count: number; amount_cents: number }
}
export interface BatchSummary {
  by_method: { method: string; items: number; amount_cents: number }[]
  by_title: { publication_slug: string; lines: number; amount_cents: number }[]
  checks_and_money_orders_cents: number
  cash_cents: number
  /** Settles through Authorize.Net: in the totals, not in the bank deposit. */
  card_and_echeck_cents?: number
  /** Deposited, then voided: still in every total above. */
  returned_after_deposit?: { n: number; order_keys: string[]; payer: { name: string | null; account_number: string | null }; method: string; reference: string; amount_cents: number }[]
}

/**
 * The line to pre-fill for a picked member: their most recently ending active
 * or lapsed title that we bill (not SimpleCirc-owned), at its 12-month plan
 * (else the first plan listed). Null leaves the line for the operator.
 */
export function prefillLine(record: Pick<MemberRecord, 'titles'>, plans: DeskPlan[]): Line | null {
  const held = record.titles
    .filter((t) => (t.state === 'active' || t.state === 'lapsed') && !t.sc_owned)
    .sort((a, b) => (b.end_date ?? '').localeCompare(a.end_date ?? ''))
  for (const t of held) {
    const offers = plans.filter((p) => p.publication_slug === t.slug)
    const p = offers.find((o) => o.term_months === 12) ?? offers[0]
    if (p) return { slug: t.slug, plan_id: p.plan_id, price_cents: p.amount_cents }
  }
  return null
}

/** Count and money of what is really deposited: voided entries out, voided lines out of the amount.
 *  A line voided after the deposit is still in `amount_cents` (the server counts it, ruling R17). */
export function runningTotals(batch: Pick<BatchDetail, 'entries'>): { count: number; amountCents: number } {
  const live = batch.entries.filter((e) => !e.voided)
  return { count: live.length, amountCents: live.reduce((s, e) => s + e.amount_cents, 0) }
}

/** `giftsReady`: every gift line has a recipient the duplicate check let through. */
export function canSaveEntry(a: {
  picked: boolean; method: ManualMethod; reference: string; amountCents: number; lines: Line[]; giftsReady?: boolean
}): boolean {
  if (!a.picked || a.lines.length === 0 || a.giftsReady === false) return false
  if (a.method !== 'cash' && !a.reference.trim()) return false
  if (!a.lines.every((l) => l.slug && l.plan_id && l.price_cents > 0)) return false
  return balance(a.method, a.amountCents, a.lines).ok && linesTotal(a.lines) === a.amountCents
}

/** A card or eCheck from a mailed-in form: charged now, so no amount or reference to key. */
export function canChargeEntry(a: {
  picked: boolean; method: 'card' | 'bank'; lines: Line[]; giftsReady?: boolean
  billZip: string; nameOnAccount: string; signed: boolean
}): boolean {
  if (!a.picked || a.lines.length === 0 || a.giftsReady === false) return false
  if (!a.lines.every((l) => l.slug && l.plan_id && l.price_cents > 0)) return false
  if (!a.billZip.trim()) return false
  if (a.method === 'bank' && (!a.nameOnAccount.trim() || !a.signed)) return false
  return linesTotal(a.lines) > 0
}

export function depositSlipText(s: Pick<BatchSummary, 'checks_and_money_orders_cents' | 'cash_cents' | 'card_and_echeck_cents'>): string {
  const slip = `The bank deposit slip should total ${dollars(s.checks_and_money_orders_cents)} in checks and money orders, plus ${dollars(s.cash_cents)} in cash.`
  return s.card_and_echeck_cents
    ? `${slip} Cards and eChecks (${dollars(s.card_and_echeck_cents)}) are not in the deposit: they settle through Authorize.Net.`
    : slip
}

/** `detail`: the method's own detail (name on the check, money order issuer, where cash came in). */
export interface Envelope { memberId: number; method: ManualMethod; reference: string; amountCents: number; lines: Line[]; detail: string }

/** The method detail a fresh batch entry starts with: cash in a Mail Order Batch came by post. */
export const batchDetailDefault = (method: ManualMethod): string => (method === 'cash' ? 'post' : '')

/**
 * Whether the next save may reuse the last attempt's form token. Only a retry
 * of the very same envelope (same member and fields) keeps it: if the first
 * try saved but the answer was lost, the replay guard then makes the retry
 * safe. Anything else is a different envelope and needs a fresh token.
 */
export function keepToken(last: Envelope | null, next: Envelope): boolean {
  if (!last) return false
  return last.memberId === next.memberId && last.method === next.method
    && last.reference.trim() === next.reference.trim() && last.amountCents === next.amountCents
    && last.detail.trim() === next.detail.trim()
    && last.lines.length === next.lines.length
    && last.lines.every((l, i) => {
      const n = next.lines[i]
      return l.slug === n.slug && l.plan_id === n.plan_id && l.price_cents === n.price_cents
        && (l.for ?? 'payer') === (n.for ?? 'payer') && recipientKey(l.rcpt) === recipientKey(n.rcpt)
    })
}

/** "Returned after deposit: #3 Robert Ashwood $59.95." — what the frozen summary still counts. */
export function returnedAfterDepositText(r: NonNullable<BatchSummary['returned_after_deposit']>): string {
  if (r.length === 0) return ''
  const items = r.map((x) => `#${x.n} ${x.payer.name ?? ''} ${dollars(x.amount_cents)}`.replace(/\s+/g, ' ').trim())
  const total = r.reduce((s, x) => s + x.amount_cents, 0)
  return `Returned after deposit (still counted above, ${dollars(total)}): ${items.join('; ')}.`
}

/** What the Void column shows. A line voided after the deposit (ruling R17)
 *  is not `voided` (its money is still counted) but must not offer Void again.
 *  A card or eCheck is refunded instead: Refund until nothing is left. */
export function entryVoidState(e: Pick<BatchEntry, 'voided' | 'returned_after_deposit' | 'via_gateway'>): 'live' | 'voided' | 'voided-after-deposit' | 'refundable' | 'refunded' {
  if (e.via_gateway) return e.voided ? 'refunded' : 'refundable'
  if (e.voided) return 'voided'
  if (e.returned_after_deposit) return 'voided-after-deposit'
  return 'live'
}

/** A card or eCheck entry in the shape the Refund dialog takes. */
export function refundItem(e: BatchEntry): HistoryItem {
  return {
    at: '', kind: 'money', what: `Entry #${e.n}`, title_abbr: null,
    detail: [e.payer.name, e.lines_text].filter(Boolean).join(' · '),
    status: '', tone: 'ok', by: '', amount_cents: e.original_amount_cents, order_key: e.order_keys[0] ?? null,
    can_void: false, can_refund: true, refund_via_gateway: true, refundable_cents: e.amount_cents, is_payer: true,
  }
}
